/**
 * `AnalyticsCanvas` — the Analytics page's content region, as a real dashboard grid.
 *
 * Cards are dragged to any position and snapped to a 12-column grid; panels are
 * resized within per-card minimums. It replaces a fixed arrangement (a tile strip
 * above a panel grid that could be one column or two) with a canvas, because that
 * arrangement could express exactly two layouts and neither was the one anybody
 * wanted for their own brand.
 *
 * ## It is content-AGNOSTIC on purpose
 *
 * The canvas never imports `Overview`, `ROICards`, `ScansByTime2` or `ScansByLocation`.
 * It takes `cards` as `AnalyticsCardItem[]` and renders whatever it is given inside
 * `AnalyticsCardShell`. That is what lets the same component serve every seeded view
 * and any user-saved one without a switch statement, and it is why adding a card is a
 * one-line change at the call site rather than an edit here.
 *
 * ## The library is react-grid-layout, and `@hello-pangea/dnd` could not have done it
 *
 * Pangea models a droppable as a single-axis LIST: it reorders, and its placeholder
 * arithmetic assumes one row or one column. There is no position in it — no x, no y,
 * no width, no height — so "put the map bottom-right at a third of the width" is not
 * a state it can hold, let alone a gesture it can express. It stays in the codebase
 * for the list reorders it is right for (`components/input/Sort`).
 *
 * v2 of react-grid-layout was chosen over the more familiar v1 for two concrete
 * reasons. It ships its OWN types, so there is no `@types/react-grid-layout` to drift
 * out of step with the runtime — the failure mode `@types/react-beautiful-dnd` is
 * already sitting in this package.json demonstrating. And its `useContainerWidth`
 * replaces v1's `WidthProvider` HOC with a hook that exposes `measureWidth()`, an
 * explicit re-measure that does not depend on a `ResizeObserver` firing. The Ask-style
 * side panel makes the canvas narrower when it opens, and that is precisely a width
 * change no window `resize` event announces.
 *
 * ## Expand is an in-page MAXIMIZE, not a modal
 *
 * The expanded card fills the content region: from immediately below the action line
 * down to the bottom of the page, inside the page's normal 24px gutter. It does not go
 * edge-to-edge, does not cover the sidebar, and does not cover the action line — the
 * filter bar stays usable, which is most of the reason to expand a chart in the first
 * place.
 *
 * Expansion is EPHEMERAL: it is state on the page and is never written into the saved
 * `TableViewState`. Restoring a view must not reopen someone inside a maximized card.
 * Card membership and geometry DO persist.
 *
 * ## 🚨 THE MEASURED CONTAINER IS RENDERED UNCONDITIONALLY. Never gate it on `expandedId`.
 *
 * This component used to `return` an entirely separate tree while a card was expanded,
 * which took `containerRef`'s div out of the DOM. That single line wiped the whole
 * dashboard on collapse, and the causal chain is worth writing down because every step
 * of it is silent:
 *
 *  1. Expanding unmounted the div. Measured: a `data-probe-id` stamped on the container
 *     was absent from the DOM while expanded, and the node present after collapse was a
 *     DIFFERENT one.
 *  2. `useContainerWidth` attaches its `ResizeObserver` in an effect keyed on
 *     `[measureWidth]`, and `measureWidth` is keyed on `[mounted]` — both stable after
 *     the first render. So the effect NEVER re-runs: the observer stays bound to the
 *     dead node and the replacement is never observed.
 *  3. The observer's last delivery for the dying node reports zero, because
 *     `getContentWidth` reads `getComputedStyle(node).width`, which is `""` for a
 *     detached element — `parseFloat` gives NaN and it falls through to
 *     `clientWidth (0) - padding`. So `width` became 0.
 *  4. The grid is gated on `mounted && width > 0`, so width 0 renders nothing.
 *  5. The zero-width recovery interval below could not save it: it fires while
 *     `containerRef.current` is null (a no-op), gives up after 20 attempts, and its
 *     deps do not change on collapse so it never restarts.
 *
 * None of that reproduces in the Claude preview pane, where `ResizeObserver` and
 * `requestAnimationFrame` fire zero times — step 3 cannot happen there, so the bug
 * reads as "works fine" in the pane and "wipes the page" in Chrome.
 *
 * Keeping the container mounted removes the whole class: the ref never dangles, the
 * observer never goes stale, and there is no state to recover from. The grid's
 * CHILDREN still unmount while expanded, which is deliberate — one chart instance, one
 * map instance, never two.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Box, Skeleton, Typography, alpha, useTheme, type Theme } from '@mui/material'
// `HandIcon`, not `PlusIcon` — see `COMPOSE_DROP_HAND_KEYFRAME`. A `+` names the RESULT
// (one more series); a hand names the ACT the reader is mid-way through, which is the
// thing an overlay that appears DURING a drag should be saying.
// `BanIcon` joins it for the REFUSED overlay — see `composeRefusedPanelSx`. A crossed
// circle names "not here" without naming blame, which a warning triangle would.
import { BanIcon, HandIcon } from 'lucide-react'
import {
  ResponsiveGridLayout,
  cloneLayout,
  getBreakpointFromWidth,
  moveElement,
  setTransform,
  useContainerWidth,
  verticalCompactor,
  type Layout,
  type LayoutItem,
  type ResponsiveGridLayoutProps,
  type ResponsiveLayouts,
} from 'react-grid-layout'

// Side-effect import. react-grid-layout positions and sizes every item through these
// classes, so without it drag renders as a jump and resize does not render at all.
// The default sheet is then overridden below — it ships a RED placeholder.
import 'react-grid-layout/css/styles.css'

import { useChromeTransitioning } from 'config'
import { vhMinus, zoomOf } from 'utils/textZoom'
import type { AnalyticsGridBreakpoint, AnalyticsGridItem, AnalyticsGridLayouts } from 'types'

import { AnalyticsCardShell, type AnalyticsCardKind, type AnalyticsCardSurface } from './AnalyticsCardShell'
import {
  ANALYTICS_CARD_SIZE_DEFAULTS,
  ANALYTICS_DRAG_ITEM_ID,
  ANALYTICS_GRID_BREAKPOINTS,
  ANALYTICS_GRID_COLS,
  ANALYTICS_GRID_MARGIN,
  ANALYTICS_ROW_HEIGHT,
  parseAnalyticsVariantCardId,
  resolveLayout,
  toPersistedItems,
  type AnalyticsCardCategory,
  type AnalyticsCardSizeSpec,
} from './analyticsGrid'
// The id GRAMMAR module, not a page component — see that file's own header for why
// pulling capability/parsing helpers in here does not violate "content-agnostic"
// (that rule is about not importing Overview/ScansByTime2/ScansByLocation; the id
// grammar this canvas already depends on for `AnalyticsCardCategory` lives beside it).
import {
  analyticsComposeTargetRefusal,
  analyticsDisplayCapability,
} from './analyticsDisplay'

export interface AnalyticsCardItem {
  /** Stable id. Must match the ids seeded views reference. */
  id: string
  /** Drives the size spec, the surface default and where the drag handle lands. */
  kind: AnalyticsCardKind
  /** Omit for a card that draws its own heading (a stat tile draws a figure block). */
  title?: string
  description?: React.ReactNode
  /** The card's own trailing header control (legend, segmented toggle, link). */
  action?: React.ReactNode
  /**
   * The card's TYPE — the pill on its library row and the value the type filter
   * matches. Typed against the registry, not a free string, so a category the filter
   * does not know about is a compile error rather than a card nobody can find.
   */
  category?: AnalyticsCardCategory
  /** One line saying what this card is, for the Customize panel's card list. */
  summary?: string
  /** Per-card override of `ANALYTICS_CARD_SIZE_DEFAULTS[kind]`. */
  size?: Partial<AnalyticsCardSizeSpec>
  /** Overrides the kind default (panels expandable, tiles not). */
  expandable?: boolean
  /**
   * `own` (the tile default) when the card draws its own `Card` — `AnalyticsBlock`.
   * `shell` (the panel default) when the card supplies only a body and lets the shell
   * draw the surface + header.
   */
  surface?: AnalyticsCardSurface
  /**
   * `expanded` — this card is MAXIMIZED, owning the whole content region.
   * `fillHeight` — the card's BOX decides its height, so take all of it.
   *
   * Two flags because they are two facts and only one of them is rare. Every panel in
   * the grid has `fillHeight` (its grid cell is its height — that is what makes a
   * vertical resize mean anything), while `expanded` is true for at most one card at
   * a time. Collapsing them would make every gridded panel claim to be maximized.
   */
  render: (ctx: { expanded: boolean; fillHeight: boolean }) => React.ReactNode
}

interface AnalyticsCanvasProps {
  /**
   * ── HEIGHT OF PAGE CHROME ABOVE THIS CANVAS THAT DISAPPEARS ON EXPAND ─────────────
   *
   * Subtracted from the click-time seed of `regionTop`, and it exists because that seed
   * is taken SYNCHRONOUSLY in the click handler — before React has re-rendered, and
   * therefore while the chrome is still in the DOM. Without it the expanded region is
   * short by exactly that chrome's height and leaves a gap at the bottom of the page for
   * one commit, which is the commit the FLIP animation samples.
   *
   * The concrete case is the ROI hero. It used to be passed INTO this component as a
   * `hero` slot precisely so that hiding it on expand was internal and the root's top
   * never moved; it now renders above the page's sticky control bar, where it belongs
   * (the Scans/Certificates toggle does not scope it, so a control above a card it does
   * not affect was misleading). Hiding it is still required — an expanded card must start
   * immediately below the action line, not below the hero — so the geometry that the slot
   * used to guarantee structurally is now supplied as this one measured number.
   *
   * ⚠️ It is the height the chrome occupies INCLUDING its bottom margin, measured by the
   * page. A constant would be wrong the moment the hero grows a caveat line.
   *
   * The `useLayoutEffect` re-measure remains the authority and will land on the same
   * value; this only stops the first commit being wrong.
   */
  collapsingChromeAbovePx?: number
  /** Every card the page can render. Membership is `cardIds`, not this. */
  cards: AnalyticsCardItem[]
  /** Ids on the canvas, from the active view. Ids with no matching card are skipped. */
  cardIds: string[]
  layouts: AnalyticsGridLayouts
  onLayoutsChange: (breakpoint: AnalyticsGridBreakpoint, items: AnalyticsGridItem[]) => void
  /** A card dropped in from the Customize panel: added to `cardIds` at that position. */
  onCardDropped?: (id: string, item: AnalyticsGridItem, breakpoint: AnalyticsGridBreakpoint) => void
  /**
   * The card id currently being dragged out of the Customize panel, or null. Set on
   * the SOURCE's `dragstart`, which is what lets the grid show a correctly-sized drop
   * placeholder before the pointer has ever entered it.
   */
  droppingCardId?: string | null
  /**
   * VRG-467 — a metric was dropped ONTO an existing chart/map card rather than onto
   * empty canvas space: it should join that card as another series instead of becoming
   * a new card. `targetCardId` is the card dropped onto; `metricId` is the dragged
   * metric's id (already resolved from whatever the drag source actually was — see
   * `composeSourceMetricId`).
   *
   * Optional, same reasoning as `onCardDropped`: a canvas rendered without it simply
   * offers no compose affordance.
   */
  onComposeDrop?: (targetCardId: string, metricId: string) => void
  /**
   * VERA-260 — the id of the CANVAS CARD currently being dragged, or null. Distinguishes
   * this from a LIBRARY row drag, which sets `droppingCardId` alone. It:
   *
   *  - excludes that card from its own set of valid compose targets (`isComposeDropTarget`
   *    guards on it);
   *  - tells `composeOffered` whose capability to consult; and
   *  - tells `handleDrop` that a drop on EMPTY canvas space MOVES this card rather than
   *    adding one — a canvas-card drag has nothing to add, the card is already here.
   *
   * `droppingCardId` is STILL set alongside this (to the same card id) — that is what lets
   * this drag reuse every bit of `isComposeDropTarget`/`composeDropScrimSx`/the discovery
   * overlay for free, exactly as a library row's drag does.
   *
   * ✅ That reuse is MEASURED, not merely intended — recorded because a claim of the form
   * "X reuses Y for free" is exactly the kind that is written when the wiring is added and
   * never checked again. Driven in the running app: a `dragstart` on the `certificates_count`
   * tile's grip put `Drop to add to this chart` on `scans_count::chart` and on nothing
   * else, and a `dragover` on `scans_count::map` produced that display's refusal
   * sentence — byte-identical overlays to a library-row drag of the same metric, because
   * they are literally the same branch of the render loop. Dragging `scans_count::chart`
   * by its own grip left THAT card bare while still refusing the map, which is gate 2
   * (self-exclusion) firing. If a future change makes a canvas-card drag stop setting
   * `droppingCardId`, all three of those go silent at once and nothing else will complain.
   */
  composeSourceCardId?: string | null
  /**
   * VERA-260 — a canvas card's `dragstart`/`dragend`. Mirrors `onCardDropped`'s
   * optionality — except that with one unified gesture these are no longer optional in
   * practice: without them a card cannot be picked up at all, so a canvas rendered without
   * them is a read-only dashboard (which is exactly what `onCardDropped`'s absence already
   * means, since `dropConfig` is gated on it).
   */
  onComposeDragStart?: (id: string) => void
  onComposeDragEnd?: () => void
  /**
   * Lifted so the action bar's controls can be disabled while expanded and the page
   * can read the state without reaching into this component.
   */
  expandedId?: string | null
  onExpandedChange?: (next: string | null) => void
  /**
   * Take a card off the canvas — the per-card `X`.
   *
   * Optional: a canvas rendered without it simply shows no remove control, which keeps
   * this component usable for a read-only dashboard rather than assuming every caller
   * has an editable view behind it.
   */
  onRemoveCard?: (id: string) => void
  /**
   * Bumped by the page whenever something outside the canvas changes its width — the
   * Customize panel opening or closing. A width change with no window `resize` behind
   * it is invisible to everything except an explicit re-measure.
   */
  widthNonce?: number
  isLoading?: boolean
}

/**
 * The page gutter, 24px. The expanded region ends this far off the bottom.
 *
 * It is `Layout`'s scroll-container padding, NOT the page's own — `AnalyticsPage` used to
 * add a second `p={6}` on top of it and no longer does, so this is now the only gutter
 * there is. The VALUE is unchanged by that removal (both were 24), and the reason is worth
 * recording: this term has always been the distance from the region's bottom to the WINDOW,
 * and the page's extra padding sat inside the region rather than below it. What moved is
 * `regionTop`, which is measured, so it corrects itself.
 */
const PAGE_GUTTER_PX = 24

/** Marks a card's grid wrapper so the expand animation can find where it started. */
const CARD_NODE_ATTR = 'data-analytics-card'
/** Marks the maximized region, for the same reason in the other direction. */
const EXPANDED_NODE_ATTR = 'data-analytics-expanded'

/**
 * 240ms on the house easing.
 *
 * The same curve `apps/app`'s page transitions use — a hard-out ramp that starts fast
 * and settles, which is what makes a box growing to fill a region read as "it moved"
 * rather than "it was replaced". 240 rather than the 120 the control fades use because
 * the distance is a whole page rather than an opacity step; below ~180 a full-region
 * grow reads as a flicker, above ~320 it starts to feel like waiting.
 */
const EXPAND_DURATION_MS = 240
const EXPAND_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * FLIP the node from a rect it is not in, to the rect it now occupies.
 *
 * Transform-and-opacity only, so the whole thing runs off the main thread and no
 * layout is recomputed per frame — which matters here because the growing box contains
 * a recharts plot or a Google map, and animating their real width would re-tile both
 * ~14 times.
 *
 * ⚠️ Deliberately NOT gated on the animation finishing anywhere. Expansion state flips
 * FIRST and this runs after the fact, so a browser that throttles, refuses or never
 * advances the animation degrades to today's instant snap. Gating the collapse on an
 * `animation.finished` promise would put the page's escape hatch behind a clock, which
 * is precisely the failure this component was just fixed for.
 */
const flipFrom = (node: HTMLElement, from: DOMRect) => {
  if (typeof node.animate !== 'function') return
  const to = node.getBoundingClientRect()
  if (!from.width || !from.height || !to.width || !to.height) return

  // The rects are PAINTED px; `translate()` below is a length on the node, so it is
  // LAYOUT px inside `#app`'s text zoom and gets scaled by z again. Divide the travel by
  // the node's zoom so the first frame sits on the source rect at every text size (the
  // scale factors are ratios of two painted lengths, so the zoom cancels out of those).
  const z = zoomOf(node)
  const dx = (from.left - to.left) / z
  const dy = (from.top - to.top) / z
  const sx = from.width / to.width
  const sy = from.height / to.height

  // Nothing perceptible to animate — skip rather than schedule a no-op that would still
  // show up in `getAnimations()` and muddy anyone reading it.
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) {
    return
  }

  /**
   * ── 🚨 A FLIP THAT STARTS OFF-SCREEN IS NOT A FLIP, IT IS A FLASH ───────────────────
   *
   * The whole premise of this animation is VISUAL CONTINUITY: the box is already on screen,
   * and the transform makes it appear to grow from where the eye last saw it. That premise
   * fails completely if the start rect is outside the viewport. What plays then is a
   * translucent panel (`opacity: 0.4`) sweeping in from beyond the edge, over a viewport
   * that is momentarily empty because the grid has already unmounted — which is exactly the
   * "weird panel flashes" this guard was added for.
   *
   * ⚠️ THIS IS NOT A HYPOTHETICAL EDGE CASE; IT WAS THE COMMON PATH. Measured on /analytics
   * at 1280×800, expanding the "Scans by time" panel: the first keyframe resolved to
   * `matrix(1.56907, 0, 0, 1.34721, 0, 697.156)` at `opacity: 0.4` — a start position 697px
   * BELOW a destination whose top was 224, i.e. y=921 in an 800px-tall viewport. Entirely
   * off-screen, on the very first expand, from a clean load.
   *
   * The cause is arithmetic rather than a mistake in this function: the grid begins ~737px
   * down the page, so ANY card in it sits below the fold in document coordinates, while the
   * expanded region always lands at ~224. `handleToggleExpand` scrolls to the top before the
   * commit, so the two rects are necessarily ~700px apart no matter where the reader was
   * scrolled when they clicked. There is no arrangement of the page in which this animation
   * had a short distance to travel.
   *
   * So: if the source does not INTERSECT the viewport, skip. The result is the instant swap
   * that this file's header already names as the acceptable degradation, and an instant swap
   * is strictly better than an animation whose first half is invisible.
   *
   * BOTH ENDPOINTS ARE TESTED, not just the source, and the COLLAPSE direction is why. There
   * the roles invert: the source is the maximized card (always on screen) and the DESTINATION
   * is the card's slot back in the grid, ~700px down and below the fold — so a source-only
   * test would let collapse animate a translucent panel sliding down out of view, which is
   * the same defect wearing the other direction. `restoreFocusId` brings the card back into
   * view via focus straight afterwards, so the instant swap is not leaving the reader
   * anywhere they cannot get to.
   *
   * Vertical only. Horizontal travel is bounded by the page gutters — a card cannot be
   * off-screen sideways — so a horizontal test would never fire and would only add a
   * condition to reason about.
   */
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const offScreen = (r: DOMRect) => r.bottom <= 0 || r.top >= viewportHeight
  if (offScreen(from) || offScreen(to)) return

  node.animate(
    [
      {
        transformOrigin: 'top left',
        transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`,
        opacity: 0.4,
      },
      { transformOrigin: 'top left', transform: 'none', opacity: 1 },
    ],
    { duration: EXPAND_DURATION_MS, easing: EXPAND_EASING, fill: 'none' },
  )
}

/**
 * ⚠️ EVERY object prop below is a module constant, and that is a CORRECTNESS
 * requirement, not tidiness. Written inline they would be new identities on every
 * render, and react-grid-layout puts `containerPadding` and `margin` straight into the
 * dependency array of the effect that recomputes its breakpoint. An inline literal
 * makes that effect run on every render.
 *
 * The same class of bug, from the library's own side, is why `compactor` is passed
 * explicitly below.
 */
const CONTAINER_PADDING: [number, number] = [0, 0]

/**
 * Indexed off the props type rather than importing `DragConfig` / `ResizeConfig`.
 * Those two interfaces exist in the package's type definitions but are NOT re-exported
 * from its entry point, so naming them directly does not compile.
 */
type GridProps = ResponsiveGridLayoutProps<AnalyticsGridBreakpoint>

/**
 * ⚠️ REACT-GRID-LAYOUT'S OWN DRAGGING IS OFF. The canvas has exactly ONE drag protocol.
 *
 * The library keeps everything else it does — the 12-column geometry, the compactor, the
 * resize handles, the drop placeholder and the live re-flow preview, all of which are
 * driven by `dropConfig` and are independent of this flag. What it no longer does is move
 * a card by pointer, because a card is now picked up with HTML5 drag-and-drop instead
 * (`ANALYTICS_DRAG_HANDLE_CLASS`), which is the only protocol with a concept of a drop
 * TARGET and therefore the only one that can let one gesture either move a card or merge
 * it into a chart depending on where it is released.
 *
 * Leaving it enabled is not an option, and this is measured rather than assumed: an
 * element that is both a `dragConfig.handle` and natively `draggable` engages both
 * protocols and strands the card mid-gesture — see `ANALYTICS_DRAG_HANDLE_CLASS` for the
 * full mechanism. `handle`/`cancel`/`threshold` are therefore not merely unused here, they
 * would be actively harmful if restored without also removing the native `draggable`.
 *
 * `onDragStop` is gone from the grid below for the same reason: it can never fire.
 * `onResizeStop` still commits, because resizing is untouched by this.
 */
const DRAG_CONFIG: NonNullable<GridProps['dragConfig']> = { enabled: false }

/**
 * Bottom-right only. A card is positioned from its top-left, so offering the north and
 * west handles means a resize that also MOVES the card — two changes from one gesture,
 * and the one nobody asked for is the move.
 */
const RESIZE_CONFIG: NonNullable<GridProps['resizeConfig']> = { enabled: true, handles: ['se'] }

/**
 * ── 🚨 WHY THE CARDS LAG THE SIDEBAR, AND WHY SUPPRESSING A TRANSITION DID NOT FIX IT ──
 *
 * VRG-512, second attempt. The first one (`config/appChromeTransition` + the
 * `transition: none` rules below) is CORRECT AS FAR AS IT GOES and is kept — but it
 * treated the wrong half of the problem, and the symptom survived it unchanged. What it
 * removed was the EASE. What was actually late was the GEOMETRY, and a stale number does
 * not become fresh by being applied instantly.
 *
 * ## The measurement, taken in the running app rather than reasoned about
 *
 * The ROI hero and the grid sit on the same page under the same sidebar, and only one of
 * them tracks it. Their DOM says why immediately:
 *
 *   ROI hero card   inline style: NONE           position: static     width: from CSS
 *   every grid card inline style: `transform: translate(506px, 0px); width: 481px;
 *                                  position: absolute`                width: from JS
 *
 * Then the decisive test — widen the drawer by 200px SYNCHRONOUSLY, no transition, and
 * read every box back in the same task before a single frame elapses:
 *
 *   ROI hero        1998 -> 1798   tracked, same layout pass
 *   grid CONTAINER  1998 -> 1798   tracked, same layout pass
 *   grid ITEM        482 ->  482   DID NOT MOVE
 *
 * The container is a normal CSS block, so it narrows with the page. Its children are
 * absolutely positioned at pixel literals React wrote from `useContainerWidth`, so they
 * cannot narrow until React re-renders. During a real transition the cards therefore
 * detach from their own container every frame — the container narrows now, the cards
 * follow one or two frames later — which is precisely "the charts, KPIs and map move
 * delayed compared to the ROI chart".
 *
 * ⚠️ AND THE LATENCY IS STRUCTURAL, NOT INCIDENTAL. `useContainerWidth`'s observer is
 * `ResizeObserver -> requestAnimationFrame -> setState`, and per the HTML spec rAF
 * callbacks run BEFORE that frame's resize-observation step. So the width a frame renders
 * with was measured in the PREVIOUS frame, and React's default-priority flush (a
 * MessageChannel macrotask) can push the commit past the paint entirely. No amount of
 * `transition: none` shortens that chain; it is the chain that is the lag.
 *
 * ## The fix: take the JS measurement out of the horizontal critical path
 *
 * A ratio is invariant under the resize; a pixel is not. So while chrome is transitioning,
 * cards are positioned with PERCENTAGE `left`/`width` against the container that is already
 * tracking correctly, and the browser resolves them in the same layout pass as the sidebar's
 * own width — exactly what the ROI hero gets for free by being in normal flow. Vertical
 * geometry stays in pixels because a horizontal resize does not change it.
 *
 * Verified by converting one card to percentages live and re-running the synchronous test:
 * that card went 481 -> 432.8 while its pixel sibling stayed at 482.
 *
 * ⚠️ THE `transition: none` SUPPRESSION IS A PREREQUISITE, NOT A LEFTOVER — do not remove
 * it as "superseded". Measured: with the vendor sheet's `transition: transform, width,
 * height 200ms` still live, a percentage WIDTH change is itself an animatable change and
 * gets eased, while `left` is not in that list and lands instantly. The card then shears —
 * its left edge arrives and its right edge follows 200ms later. The two halves of this fix
 * only work together.
 *
 * ## Why the ratio is allowed to be computed from a STALE width
 *
 * `left / containerWidth` is not perfectly scale-invariant: margins are fixed pixels while
 * the container is fluid, so a ratio captured at one width is slightly wrong at another.
 * Measured worst case — the entire 200px in ONE step — is 4.7px, and a real transition
 * moves ~17px per frame, so the intra-frame error is sub-pixel. It also cannot accumulate:
 * every measurement that lands re-bases the ratio, and the final one is exact.
 *
 * ## Why this is scoped to the transition and not made the permanent strategy
 *
 * `positionStrategy.type` is what the library derives `useCSSTransforms` from, so adopting
 * percentages permanently would move every card off `transform` and onto `left`/`top` for
 * drag re-flow and resize too — animating layout instead of the compositor, and silently
 * dropping the 200ms ease that makes a drop's re-flow legible (`left` is not in the vendor
 * sheet's transition list). None of that is broken today, so none of it is touched. The
 * swap is invisible at both edges by construction: at the start the ratio is derived from
 * the same measured width the transform was using, and by the end the measurement has
 * caught up, so both strategies compute the same box.
 */
type GridPositionStrategy = NonNullable<GridProps['positionStrategy']>

/**
 * The library's own default, named explicitly so the steady state is a deliberate choice
 * rather than whatever `positionStrategy`'s absence happens to fall back to — and so the
 * two strategies are visibly a pair.
 */
const TRANSFORM_STRATEGY: GridPositionStrategy = {
  type: 'transform',
  scale: 1,
  calcStyle: setTransform,
}

/**
 * Horizontal geometry as a RATIO of the container, vertical geometry in pixels.
 *
 * `type: 'absolute'` is required rather than cosmetic: the library reads it to decide
 * `useCSSTransforms`, and a `translate()` percentage resolves against the ELEMENT's own
 * size rather than its container, so transform-based positioning cannot express this.
 */
const fluidPositionStrategy = (containerWidth: number): GridPositionStrategy => ({
  type: 'absolute',
  scale: 1,
  calcStyle: pos => ({
    position: 'absolute',
    top: `${pos.top}px`,
    height: `${pos.height}px`,
    left: `${(pos.left / containerWidth) * 100}%`,
    width: `${(pos.width / containerWidth) * 100}%`,
  }),
})

/**
 * The nearest ancestor that actually scrolls.
 *
 * NOT `window`: the platform's `Layout` scrolls an inner `div`, so `window.scrollTo`
 * is a no-op on every page in this app. Verified in the browser — `scrollY` read 0
 * while an inner `MuiBox-root` held a `scrollTop` of 316.
 */
const scrollableAncestor = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null
  while (current) {
    const overflowY = getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

/** Merge a card's override onto its kind default. */
const sizeOf = (card: AnalyticsCardItem): AnalyticsCardSizeSpec => ({
  ...ANALYTICS_CARD_SIZE_DEFAULTS[card.kind],
  ...card.size,
})

/* ────────────────────────────────────────────────────────────────────────────────────
   VRG-467 — drag a metric ONTO an existing chart/map so it joins as another series
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The metric id a compose gesture is actually about, regardless of what the drag
 * SOURCE happened to be.
 *
 * `droppingCardId` (set on `dragstart` — see `AnalyticsCustomizePanel`/
 * `AnalyticsDisplayPicker`) is either a bare metric id (a library row) or a
 * single-metric variant id (a "Display as" chip) — both are ALWAYS draggable once capable,
 * regardless of whether the metric is already placed somewhere (VERA-260: a metric may
 * legitimately sit on several charts/maps at once, so "already placed" no longer
 * withdraws the drag affordance — see both components' own notes). A composed card is
 * NEVER itself a drag source — `AnalyticsCustomizePanel`'s `metricRows` filters every
 * variant id out of the list, composed ones included — so `metricIds` here is always
 * exactly one entry; `baseId` is that entry.
 */
const composeSourceMetricId = (droppingId: string): string =>
  parseAnalyticsVariantCardId(droppingId)?.baseId ?? droppingId

/**
 * ── IS THIS CARD A VALID DROP TARGET, AND IF NOT, IS THERE ANYTHING TO SAY? ─────────
 *
 * ⚠️ ONE FUNCTION ANSWERING BOTH, deliberately. The gate and the explanation used to be
 * separable in principle and the explanation simply did not exist: a refused card rendered
 * NOTHING, so a reader who dragged a metric at a map watched every chart light up, watched
 * the map stay dark, and was told nothing about which of two facts they had just
 * discovered — that a map never takes a second measure, or that THIS metric cannot be
 * mapped. Splitting the verdict from its reason across two functions is how those answers
 * drift apart, so they are one return value.
 *
 * `reason: null` is NOT "no reason" — it means **stay silent**, and the distinction is the
 * design. The first three gates reject cards the reader never aimed at (a KPI tile, the
 * card they are dragging, a non-variant id). Explaining those would put a paragraph on
 * every tile on the canvas the moment a drag began, which is the noise the discovery
 * overlay's own header rules out ("incompatible cards must show NOTHING"). That rule is
 * kept, and narrowed to what it was actually protecting: the DISCOVERY pass, where the
 * eligible set must not have competition. A reader who then parks the pointer on a
 * plausible target — a chart, a map, a table — has asked a question, and gets an answer.
 *
 * The gates, in order:
 *
 *   1. `card.kind === 'panel'` — only a chart, map or table has series to join. SILENT.
 *   2. VERA-260: not the card being dragged by its own compose handle. A statement about
 *      IDENTITY rather than series membership, so it holds even for a future multi-metric
 *      source that gate 5 would not catch. SILENT.
 *   3. `card.id` parses as a variant — it IS a chart/map/table, not a bare metric row at
 *      panel size. SILENT: a card with no variant grammar is not a target the reader can
 *      reason about.
 *   4. ⚠️ THE DISPLAY MUST ACCEPT A SECOND MEASURE — `analyticsComposeTargetRefusal`, and
 *      this gate is where the old `parsed.display !== 'chart'` lived. That line, and its
 *      twin in `buildComposedDisplayVariantCard`, were TWO expressions of one rule, each
 *      under a paragraph describing a world that had already expired: both said composing
 *      onto a map was blocked because `ScansByLocation` takes a single `dataType` and no
 *      second metric was mappable anyway. `MAP_RENDERABLE` outgrew the second half of that
 *      (`invalid_scans` is mappable), and the choropleth landing beside it outgrew the
 *      framing entirely — "by country" is the map at a different GROUPING, not a different
 *      card. The rule now lives in `analyticsDisplay`, both call sites derive from it, and
 *      the sentence it returns distinguishes "no grouping could ever place this" from
 *      "mappable, just not together". See `AnalyticsMapGrouping`.
 *   5. Not already one of the target's series — `a+a::chart` is meaningless.
 *   6. The dragged metric is CAPABLE of the target's display.
 *
 * Gates 4–6 all have something to say, so all three return a sentence. Stating capability
 * BEFORE the gesture rather than failing one after is the same discipline
 * `AnalyticsDisplayPicker` applies to its chips and `analyticsDisplay` argues at length:
 * an affordance that lights up and then refuses is worse than one that never lit up.
 */
type ComposeDropVerdict = { ok: true } | { ok: false; reason: string | null }

/** Refused, with nothing worth saying — see the header on why silence is a design choice. */
const COMPOSE_DROP_SILENT: ComposeDropVerdict = { ok: false, reason: null }

const composeDropVerdict = (
  card: AnalyticsCardItem,
  droppingId: string,
  composeSourceCardId?: string | null,
): ComposeDropVerdict => {
  if (card.kind !== 'panel') return COMPOSE_DROP_SILENT
  if (composeSourceCardId && card.id === composeSourceCardId) return COMPOSE_DROP_SILENT
  const parsed = parseAnalyticsVariantCardId(card.id)
  if (!parsed) return COMPOSE_DROP_SILENT

  const metricId = composeSourceMetricId(droppingId)

  const displayRefusal = analyticsComposeTargetRefusal(parsed.display, metricId)
  if (displayRefusal) return { ok: false, reason: displayRefusal }

  if (parsed.metricIds.includes(metricId)) {
    return { ok: false, reason: 'This measure is already on this chart.' }
  }

  const capability = analyticsDisplayCapability(metricId)[parsed.display]
  if (!capability.capable) return { ok: false, reason: capability.reason }

  return { ok: true }
}

/** Is `card` a valid drop TARGET for whatever is currently being dragged? */
const isComposeDropTarget = (
  card: AnalyticsCardItem,
  droppingId: string,
  composeSourceCardId?: string | null,
): boolean => composeDropVerdict(card, droppingId, composeSourceCardId).ok

/**
 * Is `card` eligible to be a compose SOURCE — i.e. should
 * `AnalyticsCardShell.composeSourceEnabled` be true for it? (VERA-260)
 *
 * Two gates:
 *   1. For a PANEL, it must carry exactly one series. A composed (multi-metric) chart
 *      or map is not offered as a source — see `AnalyticsCardShell`'s header for why
 *      dragging one is left unanswered rather than guessed at. A TILE has no variant
 *      grammar at all (`parseAnalyticsVariantCardId` returns null for a bare metric
 *      id), which trivially satisfies "exactly one metric".
 *   2. The resolved metric must be capable of `chart` display — the only display
 *      `isComposeDropTarget` ever offers as a target, so a metric that could never
 *      legally land on ANY target has nothing to offer this gesture at all.
 */
const isComposeSource = (card: AnalyticsCardItem): boolean => {
  if (card.kind === 'panel') {
    const parsed = parseAnalyticsVariantCardId(card.id)
    if (!parsed || parsed.metricIds.length !== 1) return false
  }
  const metricId = composeSourceMetricId(card.id)
  return analyticsDisplayCapability(metricId).chart.capable
}

/**
 * The compose-drop highlight — a brand ring, the same "about to activate this" language
 * §34 gives an action row (border + ring on hover), not the selectable-card tint: this
 * target is a one-shot activation (drop here NOW), not a persistent selection state.
 */
const COMPOSE_DROP_TARGET_SX = {
  outline: '2px solid',
  outlineColor: 'brand.600',
  outlineOffset: -2,
  borderRadius: 3,
} as const

/**
 * ── THE COMPOSE-DROP DISCOVERY OVERLAY (VERA-260) ──────────────────────────────────
 *
 * The 2px ring above told you where you'd LANDED; it never told you where you COULD
 * land. Finding a valid target meant dragging over every card on the canvas and
 * watching for the ring to appear — there was no way to see the full set of places a
 * drop would do something before you started hunting for one.
 *
 * Every ELIGIBLE card (`composeEligible`, computed per card in the render loop below)
 * now dims and shows a "Drop to add to this chart" card the moment the drag starts —
 * not only on hover — so the whole set of valid targets is visible before the pointer
 * has moved anywhere. `composeHoverId === card.id` then INTENSIFIES it, alongside the
 * existing outline ring.
 *
 * The scrim-plus-centred-affordance shape is not invented here — it is
 * `FileUploadField`'s own thumbnail hover-to-zoom overlay
 * (`components/input/fields/FileUploadField.tsx`, the `thumb-overlay` class:
 * `alpha('#000000', 0.45)` behind a centred white glyph), reused rather than a new
 * pattern (§28/§47): a dark scrim naming what a drop will do is already this app's
 * language for "something is about to happen here," so the same treatment is applied
 * rather than a fresh one invented for this gesture. `#000000` rather than a theme
 * palette colour for the same reason FileUploadField uses it — the scrim's job is to
 * recede the CONTENT beneath it (a live chart, any colour), not to read as a themed
 * surface of its own; a themed "dark" isn't reliably darker than a light-mode chart.
 *
 * Incapable cards render nothing here (`composeEligible` gates it) — see the render
 * loop for why that absence is itself the "this one can't take it" signal (VERA-260's
 * task notes: "incompatible cards must show NOTHING").
 *
 * ## Two layers: a GLASS FROST over the chart, a GLASS CARD in the centre
 *
 * ### ⚠️ THE DARK WASH IS GONE. It was the thing that read as "greyed out" (VERA-260)
 *
 * 🚨 The diagnosis, recorded because the arrangement it replaces was deliberate and
 * documented, and the previous version of this header argued FOR it at length.
 *
 * The scrim used to be `alpha('#000000', 0.45→0.55)` — a 45–55% black wash borrowed from
 * `FileUploadField`'s thumbnail overlay — over a token `blur(3px)`. At that ratio the
 * WASH is doing essentially all the work and the blur is a rounding error: what a reader
 * saw was a card turning dark grey, which is the app's established language for DISABLED,
 * not for "this is a live target". The user's report was exactly that — the target "dims
 * with a grey background" — and the two readings are genuinely hard to tell apart,
 * because a 50% black wash over a chart and a disabled chart look the same.
 *
 * The borrowing was also the wrong precedent to follow. `FileUploadField`'s scrim covers a
 * THUMBNAIL and its job is to obliterate it so a white glyph reads; this covers a LIVE
 * CHART the reader is choosing a target from, and obliterating it removes the very thing
 * they are aiming at. So the layers are re-weighted rather than restyled: the blur goes UP
 * (3px -> 14px) and does the receding, and the wash comes down to a faint frost tint that
 * only buys legibility.
 *
 *  - The SCRIM (`composeDropScrimSx`) is now real frosted glass: `blur(14px)` plus
 *    `saturate(140%)` and a ~14–22% tint. The saturation is what stops a heavy blur
 *    reading as grey — blurring alone averages a chart's colours toward mud, and pushing
 *    saturation back up keeps the frost recognisably tinted by the chart beneath it, which
 *    is the difference between "frosted" and "greyed out". The chart's SHAPE stays legible
 *    through it; its detail does not, which is the correct division for a target you are
 *    aiming at rather than reading.
 *  - The CENTRED CARD is a larger glass panel with its own stronger `blur(20px)`, a
 *    hairline border, a soft shadow and an ANIMATED HAND (see `COMPOSE_DROP_HAND_KEYFRAME`)
 *    — the same rounded-card, centred-icon-and-text language `FileUploadField`'s dropzone
 *    uses, in glass, at a size that reads as a dropzone rather than as a toast.
 *
 * Both `backdropFilter` declarations carry the `WebkitBackdropFilter` twin — Safari
 * (and Chrome pre-76) ignore the unprefixed property outright, which would silently
 * flatten both layers back to a plain tint there. Neither declaration threatens the
 * drop mechanics: `backdrop-filter` is purely visual and does not affect hit-testing,
 * so it changes nothing about `pointerEvents: 'none'` letting every event fall through
 * to the wrapper's own `onDragOver`/`onDrop`.
 *
 * ### ⚠️ IT IS THEME-AWARE NOW, AND IT HAD TO BECOME SO — this is not a style preference
 *
 * The previous header made a specific, correct argument for theme-INDEPENDENCE: the glass
 * panel's contrast partner was never the app's surface, it was the always-dark scrim, so a
 * translucent-white panel read as frosted glass in both modes and a panel built from
 * `background.paper` would have been near-black-on-black in dark mode.
 *
 * That argument rested entirely on the wash being dark. Removing the wash removes its
 * premise: with a blur-first scrim the panel's contrast partner becomes THE CHART, whose
 * brightness follows the theme. A translucent-white panel with white text over a blurred
 * LIGHT-mode chart is white-on-white — unreadable — so keeping the old tokens after
 * removing the wash would not have been "leaving it theme-independent", it would have been
 * a legibility bug in light mode. Both layers therefore derive from `background.default` /
 * `background.paper` and the text from `text.primary`, which is what makes the panel opaque
 * ENOUGH (72%) to guarantee contrast against either.
 *
 * The 72% is the load-bearing number and it is a floor, not a taste: below roughly 60% the
 * chart's own lines start showing through the label at a contrast that fails on a busy
 * card, and the panel stops being a surface. Glass reads as glass from the BLUR and the
 * border, not from being maximally transparent.
 */
const COMPOSE_DROP_LABEL = 'Drop to add to this chart'

/**
 * ── THE REFUSED-TARGET OVERLAY (VERA-260) — the other half of the discovery fix ─────
 *
 * The overlay above answers "where CAN I drop this". This answers the question a reader
 * asks immediately afterwards and previously could not: "why not HERE?".
 *
 * ## Why it is hover-only, when the eligible overlay is not
 *
 * The two appear on different triggers on purpose, and the asymmetry is the design:
 *
 * | | eligible | refused |
 * |---|---|---|
 * | shows | the moment the drag starts, on every valid target | only while the pointer rests on it |
 * | says | four words | a sentence |
 *
 * A drag is a continuous gesture with a button held down, so the reader cannot stop to
 * READ during the discovery pass — which is exactly why the eligible affordance is a
 * moving hand and four words rather than prose. Rendering a sentence on every refused
 * panel at drag start would put text the reader cannot read in competition with the
 * targets they can use, and would make the canvas noisier the FEWER options they have. So
 * refusals wait to be asked; parking the pointer on a card is that question.
 *
 * That preserves what "incompatible cards must show NOTHING" was actually protecting — an
 * uncontested discovery pass — rather than the literal reading, which left the product
 * unable to explain itself at all. Cards the reader never aimed at still show nothing at
 * any time: `composeDropVerdict` returns `reason: null` for those, and this renders only
 * when there is a sentence.
 *
 * ## ⚠️ THE ONE EXCEPTION: zero eligible targets (`noEligibleComposeTargets`)
 *
 * Every word above is an argument about COMPETITION — prose must not crowd out the targets
 * the reader can actually use. That argument has a precondition, and it is worth naming
 * because it is invisible in the common case: it assumes such targets exist.
 *
 * When NONE do, the hover gate stops protecting a discovery pass and starts suppressing
 * the only answer available. The canvas lights up nothing whatsoever, which is
 * indistinguishable from a broken drag — the exact "nothing happened" failure this overlay
 * was built to end, surviving in the case where the reader is least able to guess why.
 * There is nothing left to compete with, so the cost the gate was buying has gone to zero
 * while its cost stays.
 *
 * So refusals with a sentence render immediately when the whole canvas refuses. The change
 * is deliberately narrow — it moves WHEN a sentence appears and never WHICH cards own one,
 * and behaviour is byte-identical the moment a single eligible target exists.
 *
 * ## ⚠️ ITS HANDLER MUST NOT `preventDefault` OR `stopPropagation` — load-bearing
 *
 * The eligible handler calls both: `preventDefault` is what marks an element as a drop
 * target at all, and `stopPropagation` stops the drop ALSO bubbling to the grid
 * container's own drop-to-canvas gesture and creating a second card from one drop. A
 * refused card must do NEITHER — it is not a drop target, so the browser should keep
 * showing the "no drop" cursor, and the event must keep reaching the grid so an ordinary
 * drop-to-canvas over this card behaves exactly as it did before this overlay existed.
 * The refused handler therefore only sets state.
 *
 * Its hover id is SEPARATE from `composeHoverId` for the same reason: that one drives
 * `COMPOSE_DROP_TARGET_SX`, the 2px brand ring meaning "release here". Putting a refused
 * card into it would ring a card that cannot take the drop — an affordance asserting the
 * opposite of the sentence inside it.
 */
const COMPOSE_REFUSED_BLUR = 'blur(10px) saturate(120%)'

const composeRefusedPanelSx = (theme: Theme) =>
  ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    px: 8,
    py: 6,
    minWidth: 220,
    maxWidth: '80%',
    borderRadius: 4,
    border: '1px solid',
    /*
      NEUTRAL, never `error.main`. A refusal here is not a failure the reader caused — most
      of these sentences are statements about what Vera records, and several are permanent
      facts about the data model. Red would read as "you did something wrong" for a gesture
      whose entire purpose was to find out whether it was possible.
    */
    borderColor: alpha(theme.palette.text.primary, 0.18),
    // The same opacity floor the eligible panel documents: legibility over a live chart in
    // either theme is the constraint, and it does not change with the message.
    backgroundColor: alpha(theme.palette.background.paper, 0.82),
    backdropFilter: COMPOSE_REFUSED_BLUR,
    WebkitBackdropFilter: COMPOSE_REFUSED_BLUR,
    boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.28)}`,
    color: 'text.secondary',
    textAlign: 'center',
  }) as const

/**
 * The frost applied to the chart itself, through the scrim.
 *
 * `saturate(140%)` is not decoration — see the header. A blur alone averages adjacent
 * colours toward grey, which is precisely the read being fixed here; restoring saturation
 * keeps the frost tinted by the chart under it.
 */
const COMPOSE_DROP_SCRIM_BLUR = 'blur(14px) saturate(140%)'
/** Blur on the glass panel's OWN backdrop — stronger, since it must read as glass by itself. */
const COMPOSE_DROP_GLASS_BLUR = 'blur(20px) saturate(180%)'

/**
 * ── THE ANIMATED HAND ───────────────────────────────────────────────────────────────
 *
 * A hand that repeatedly lowers something and releases it, over a target ring that
 * pulses as the hand reaches the bottom of its travel. It replaces a static `PlusIcon`.
 *
 * ## Why a hand rather than a `+`
 *
 * The two name different things. `+` names the RESULT — one more series on this chart —
 * which the label beside it already says in words ("Drop to add to this chart"), so the
 * glyph was spending the only pre-verbal channel available to repeat the sentence next
 * to it. The hand names the ACT, and the act is the thing in doubt: this overlay appears
 * on EVERY eligible card the moment a drag starts, so its job is "release here", which
 * is a gesture, not a quantity.
 *
 * Motion carries that better than any static glyph can. A drag is itself a continuous
 * gesture, and a target that moves in the same rhythm reads as participating in it.
 *
 * ## ⚠️ CSS KEYFRAMES, NOT A JS/OBSERVER-DRIVEN ANIMATION — this is load-bearing here
 *
 * The Claude preview pane fires `requestAnimationFrame` and `ResizeObserver` ZERO times
 * (this file's own header documents the grid bug that fact hides). Anything driven by a
 * rAF loop, a spring library, or an `IntersectionObserver` trigger would therefore never
 * start in the pane — it would look like a static icon while being "verified", and the
 * defect would only appear in a real browser. CSS `@keyframes` are driven by the
 * compositor and run regardless, so this animates in both.
 *
 * Verify it with `document.getAnimations()` rather than by eye: an animation that is
 * running and one that is paused on frame 0 are indistinguishable in a screenshot.
 *
 * ## Two keyframes, deliberately not one
 *
 * The hand's travel and the ring's pulse are separate animations sharing one `1.6s`
 * period, rather than one animation on a wrapper. They have to be: they animate
 * different properties on different elements (the hand translates, the ring scales and
 * fades), and the ring's pulse peaks at 55% — as the hand bottoms out — which a single
 * timeline could only express by animating both elements' properties in lockstep.
 *
 * Both return to identity so nothing is left displaced if the animation is interrupted
 * mid-cycle, which is the same rule `navIconMotion`'s keyframes follow.
 */
const COMPOSE_DROP_HAND_KEYFRAME = 'veraComposeDropHand'
const COMPOSE_DROP_RING_KEYFRAME = 'veraComposeDropRing'

/**
 * The hand + its target ring, as one self-contained block.
 *
 * `prefers-reduced-motion: reduce` KILLS both animations and leaves the hand resting at
 * identity over a static ring — the affordance is still fully legible without motion,
 * because the label carries the instruction and the glyph carries the gesture. Honouring
 * the query by removing the animation rather than by hiding the icon is the point: a
 * reader who asked for less motion asked for less motion, not for less information.
 */
const ComposeDropHand = ({ hovered }: { hovered: boolean }) => (
  <Box
    aria-hidden
    sx={{
      position: 'relative',
      width: 56,
      height: 56,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,

      [`@keyframes ${COMPOSE_DROP_HAND_KEYFRAME}`]: {
        '0%': { transform: 'translateY(-5px)' },
        // The release: the hand reaches the bottom of its travel and opens slightly.
        '55%': { transform: 'translateY(4px)' },
        '70%': { transform: 'translateY(2px)' },
        '100%': { transform: 'translateY(-5px)' },
      },
      [`@keyframes ${COMPOSE_DROP_RING_KEYFRAME}`]: {
        '0%': { transform: 'scale(0.8)', opacity: 0.25 },
        // Peaks as the hand bottoms out — the ring reacting to the drop, not leading it.
        '55%': { transform: 'scale(1)', opacity: 0.7 },
        '100%': { transform: 'scale(0.8)', opacity: 0.25 },
      },

      '@media (prefers-reduced-motion: reduce)': {
        '& > *': { animation: 'none !important' },
      },
    }}
  >
    {/* The target the hand drops ONTO. Dashed, borrowing `FileUploadField`'s dropzone
        language — a dashed outline is already this app's "put something here". */}
    <Box
      component="span"
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: '2px dashed',
        borderColor: hovered ? 'brand.500' : 'text.secondary',
        animation: `${COMPOSE_DROP_RING_KEYFRAME} 1.6s ease-in-out infinite`,
      }}
    />
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        color: hovered ? 'brand.500' : 'text.primary',
        animation: `${COMPOSE_DROP_HAND_KEYFRAME} 1.6s ease-in-out infinite`,
      }}
    >
      <HandIcon size={26} strokeWidth={1.25} />
    </Box>
  </Box>
)

const composeDropScrimSx = (theme: Theme, hovered: boolean) => ({
  position: 'absolute' as const,
  inset: 0,
  zIndex: 2,
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: 3,
  // Purely visual — the wrapper Box's own onDragOver/onDrop (below) must keep receiving
  // every event for this card's whole area, which pointer-events:none guarantees by
  // taking this layer out of hit-testing entirely rather than relying on bubbling.
  pointerEvents: 'none' as const,
  /*
    A FROST TINT, not a dim. `background.default` rather than `#000000`, so light mode
    frosts toward white and dark mode toward near-black — in both cases toward the
    surface the card already sits on, which is what makes it read as the card receding
    rather than as the card being switched off.

    Hover only lifts it from 0.14 to 0.22. The hover signal a reader actually acts on is
    the 2px brand ring (`COMPOSE_DROP_TARGET_SX`) plus the panel's own border going
    brand — the tint step is reinforcement, so it stays small enough not to reintroduce
    the dimming this change removed.
  */
  backgroundColor: alpha(theme.palette.background.default, hovered ? 0.22 : 0.14),
  backdropFilter: COMPOSE_DROP_SCRIM_BLUR,
  WebkitBackdropFilter: COMPOSE_DROP_SCRIM_BLUR,
  transition: 'background-color 120ms ease',
})

export const AnalyticsCanvas = ({
  collapsingChromeAbovePx = 0,
  cards,
  cardIds,
  layouts,
  onLayoutsChange,
  onCardDropped,
  droppingCardId,
  onComposeDrop,
  composeSourceCardId = null,
  onComposeDragStart,
  onComposeDragEnd,
  expandedId = null,
  onExpandedChange,
  onRemoveCard,
  widthNonce = 0,
  isLoading = false,
}: AnalyticsCanvasProps) => {
  // For the compose-drop discovery overlay below — see `COMPOSE_DROP_SCRIM_SX`.
  const theme = useTheme()

  /**
   * `measureBeforeMount` so the FIRST layout is computed from a real measurement.
   *
   * Without it the grid mounts at the hook's 1280px default, lays every card out
   * against a width the page does not have, and then re-lays them out when the
   * measurement lands — a visible reflow, and one extra pass through the library's
   * breakpoint effect on every page load.
   *
   * `mounted` gates only the GRID. `containerRef` stays attached either way, because a
   * ref that is not in the DOM can never be measured and the gate would never open.
   */
  const { width, mounted, containerRef, measureWidth } = useContainerWidth({
    measureBeforeMount: true,
  })

  /**
   * Re-measure on the two events a `ResizeObserver` alone does not reliably cover
   * here: the side panel opening (no window resize, and observers are not guaranteed
   * to have flushed in every environment this is verified in) and an actual window
   * resize. Cheap — `measureWidth` no-ops when the width has not changed.
   */
  useEffect(() => {
    measureWidth()
    window.addEventListener('resize', measureWidth)
    return () => window.removeEventListener('resize', measureWidth)
  }, [measureWidth, widthNonce])

  /**
   * Recover from a FIRST measurement of zero.
   *
   * `useContainerWidth` measures in a mount effect, which in a normal browser runs
   * after layout and returns a real number. It does not when the page mounts in a tab
   * that has not been laid out yet — a restored background tab, a `display: none`
   * ancestor, and the preview pane this work is verified in, all of which hand back a
   * container with no box. Measured here: the grid mounted at width 0 and laid four
   * stat tiles on top of each other at 162px.
   *
   * The mechanism that fixes this in a real browser is the `ResizeObserver` the hook
   * already attaches, which fires the moment the element gains a size. It is also
   * precisely the mechanism that fires ZERO times in the preview pane, so relying on
   * it alone means the grid is correct in Chrome and broken everywhere this gets
   * verified. This retries until the container has a box.
   *
   * It is bounded twice over and cannot become a poll: it terminates on the first
   * non-zero width (the effect re-runs on the `width` change and its cleanup clears
   * the timer), and it gives up after 20 attempts regardless. A container still
   * measuring zero after a second is genuinely not on screen.
   */
  useEffect(() => {
    if (width > 0) return
    let attempts = 0
    const timer = window.setInterval(() => {
      measureWidth()
      if (++attempts >= 20) window.clearInterval(timer)
    }, 50)
    return () => window.clearInterval(timer)
  }, [width, measureWidth])

  /**
   * True while the CONTAINER's width is changing — the app sidebar collapsing, the
   * customize panel sliding, a window drag.
   *
   * ## Why a container resize must NOT use the item transition
   *
   * `.react-grid-item` carries `transition: transform, width, height 200ms ease`, and
   * that is right for what it was written for: dragging one card re-flows its
   * neighbours, and easing them makes the re-flow legible.
   *
   * It is wrong for a container resize, because a container resize is not a layout
   * CHANGE — every card keeps the same grid cell. Only the pixel size of a cell moves,
   * and it moves because something else (the sidebar) is already animating. Easing on
   * top of that animates an animation: each measured width hands every item a new
   * target and restarts a fresh 200ms ease, so the cards lag the container the whole
   * way and keep going for ~200ms after it stops.
   *
   * That lag is what "the cards expand at a different speed to the chart or map" is.
   * The frames are all identical — measured, every `.react-grid-item` reports
   * `transform, width, height / 0.2s / ease`. What differs is the CONTENT:
   *
   *   - a stat tile is HTML, laid out BY its frame, so it is exactly as late as the
   *     frame is and the two never disagree;
   *   - a chart is a recharts SVG sized in JS from an observed width, and the map is
   *     Google tiles re-read on its own schedule. Both follow the frame's ACTUAL
   *     interpolating box rather than being laid out by it.
   *
   * So the eased frames make the two classes disagree, and the disagreement is visible
   * precisely because they sit side by side. Zero transition removes it by
   * construction: every card tracks the container 1:1, at whatever speed the sidebar
   * itself is moving, and content stays in register with its own frame. "Same speed"
   * is then not a duration two things have to agree on — it is the absence of a second
   * animation.
   *
   * The flag clears shortly after the last width change, so a drag-reorder that starts
   * after a resize still animates normally.
   */
  const [observedResizing, setObservedResizing] = useState(false)
  const lastMeasuredWidthRef = useRef(width)
  useEffect(() => {
    if (lastMeasuredWidthRef.current === width) return
    lastMeasuredWidthRef.current = width
    setObservedResizing(true)
    /*
      Long enough to span the gap between two frames of the sidebar's own transition
      (~16ms at 60fps, and observer delivery is not guaranteed every frame), so the flag
      stays continuously set for the whole animation instead of flickering off between
      measurements. Short enough that it is released well before any deliberate gesture
      that follows.
    */
    const timer = window.setTimeout(() => setObservedResizing(false), 120)
    return () => window.clearTimeout(timer)
  }, [width])

  /**
   * ── THE PUSHED HALF, WHICH IS THE HALF THAT COVERS THE FIRST FRAME (VRG-512) ───────
   *
   * `observedResizing` above is correct in intent and structurally one render LATE. It
   * is set from a passive effect keyed on the MEASURED width, so it cannot be true until
   * after a commit that has already written every item's new position — with the 200ms
   * ease still in force. The first frame of every sidebar toggle is the largest single
   * jump, so easing exactly that one and no others is what made the symptom read as "the
   * cards drag at the start and then catch up".
   *
   * `useChromeTransitioning()` is the sidebar SAYING SO, from a layout effect, before
   * the first frame paints. See `config/appChromeTransition`.
   *
   * Both are kept, because they answer different questions and neither subsumes the
   * other: the pushed flag covers chrome the app controls (the sidebar today, the Ask
   * panel next), and the observed flag still covers width changes nothing announces —
   * a window drag, a devtools open, a zoom change.
   */
  // ⚠️ The hook is called unconditionally and combined afterwards. Written as
  // `observedResizing || useChromeTransitioning()` it would be a CONDITIONAL hook call —
  // `||` short-circuits, so the hook would be skipped on every render where the observed
  // flag is already true, which is a rules-of-hooks violation and a real one here (that
  // flag genuinely alternates during a resize).
  const chromeTransitioning = useChromeTransitioning()
  const containerResizing = observedResizing || chromeTransitioning

  /**
   * Percentage geometry while the container is resizing, transforms the rest of the time —
   * see `fluidPositionStrategy` for the measurements behind this and for why the swap is
   * visually a no-op at both edges.
   *
   * Keyed on `width` as well as the flag, deliberately: each measurement that lands during
   * the transition RE-BASES the ratio, which is what keeps the small fixed-margin error
   * from accumulating across a long resize.
   *
   * `width > 0` guards the division. A zero width is a real state here — see the
   * zero-width recovery effect above — and a `NaN%` left would take every card off screen.
   */
  const positionStrategy = useMemo<GridPositionStrategy>(
    () => (containerResizing && width > 0 ? fluidPositionStrategy(width) : TRANSFORM_STRATEGY),
    [containerResizing, width],
  )

  const breakpoint = useMemo<AnalyticsGridBreakpoint>(
    () => getBreakpointFromWidth(ANALYTICS_GRID_BREAKPOINTS, width),
    [width],
  )

  const setExpandedId = useCallback(
    (next: string | null) => onExpandedChange?.(next),
    [onExpandedChange],
  )

  const rootRef = useRef<HTMLDivElement | null>(null)

  /** Height of the expanded region. Declared here because the click handler seeds it. */
  const [regionTop, setRegionTop] = useState(0)

  /**
   * The rect the next expand/collapse should animate FROM, captured on the CLICK.
   *
   * A ref rather than state because it is read once, in the layout effect of the render
   * the click caused, and putting it in state would schedule a second render to store
   * something that has already been consumed.
   */
  const pendingFlip = useRef<{ id: string; rect: DOMRect } | null>(null)

  const handleToggleExpand = useCallback(
    (id: string, next: boolean) => {
      const root = rootRef.current
      const source = next
        ? root?.querySelector<HTMLElement>(`[${CARD_NODE_ATTR}="${CSS.escape(id)}"]`)
        : root?.querySelector<HTMLElement>(`[${EXPANDED_NODE_ATTR}]`)
      const rect = source?.getBoundingClientRect()

      // Everything below happens in ONE handler, before either state write, because
      // the animation needs a stable frame to measure against and the two writes below
      // batch into a single render. Doing the scroll (and therefore the `regionTop`
      // measurement) in an effect instead would leave the expanded region rendering at
      // the wrong height for one commit — exactly the commit the animation samples.
      //
      // ⚠️ The scroll offset is deliberately NOT captured here any more. It existed solely
      // to re-base `rect` below; see that block for why the re-basing was the bug.
      if (next) {
        // See the `regionTop` effect for why the scroll comes first.
        scrollableAncestor(root)?.scrollTo({ top: 0 })
        // MINUS the chrome that is about to disappear. This measurement is taken before
        // React re-renders, so the ROI hero above this canvas is still in the DOM and
        // still contributing its height to the root's top — see
        // `collapsingChromeAbovePx`. Clamped at 0 so a stale or over-large value can only
        // ever cost the region a little height, never push it above the viewport (which
        // is the failure the scroll-to-top above exists to prevent).
        // LAYOUT px: `regionTop` feeds `vhMinus(regionTop + …)`, a length inside `#app`'s
        // text zoom, so the painted rect top is divided by the root's zoom first.
        // `collapsingChromeAbovePx` is already layout px (`useMeasuredHeight`).
        setRegionTop(
          Math.max(
            0,
            (root?.getBoundingClientRect().top ?? 0) / zoomOf(root) - collapsingChromeAbovePx,
          ),
        )
      }

      /**
       * ── 🚨 THE SOURCE RECT IS KEPT IN VIEWPORT COORDINATES. DO NOT RE-BASE IT ──────────
       *
       * This used to add `scrolled`, on the reasoning that the rect was captured before the
       * scroll-to-top while the target is measured after it, so the two had to be brought
       * into one frame. The conversion is arithmetically correct and visually wrong, and it
       * was the cause of the "weird panel flashes" report.
       *
       * A FLIP is a claim about what the EYE last saw, not about where a node sits in the
       * document. The last painted frame before this handler ran showed the card at its
       * VIEWPORT position — say y=200, with the page scrolled 721px down. Re-basing turned
       * that into 200 + 721 = 921, i.e. the card's document position, which after the
       * scroll-to-top is ~120px BELOW the fold of an 800px viewport. So the animation began
       * from a place the reader had never seen anything, and its first frames played
       * entirely off-screen.
       *
       * ⚠️ AND IT WAS NOT SELF-CANCELLING. The re-based value is `documentTop(card)`, which
       * is invariant in the scroll position — 921 whether the reader had scrolled to the card
       * or not — while the destination is always ~224. So every expand travelled the same
       * ~700px regardless of where the reader was, and there was no scroll offset at which
       * the animation looked right. Measured: `matrix(1.56907, 0, 0, 1.34721, 0, 697.156)`.
       *
       * Unconverted, the source is where the card was on screen when it was clicked, the
       * destination is where the expanded region lands, and the distance between them is
       * whatever the reader actually sees move — which is the only quantity a FLIP is
       * allowed to care about. `flipFrom`'s off-screen guard then catches the residual cases
       * (a keyboard or programmatic expand of a card below the fold) rather than them being
       * the norm.
       */
      pendingFlip.current = rect ? { id, rect } : null

      setExpandedId(next ? id : null)
    },
    [setExpandedId, collapsingChromeAbovePx],
  )

  const cardById = useMemo(() => new Map(cards.map(c => [c.id, c])), [cards])

  // Ids the view asks for that this page can actually render. Filtered rather than
  // rendered as a hole, so a stale saved view degrades to a shorter page, never to a
  // broken one.
  const visible = useMemo(
    () => cardIds.map(id => cardById.get(id)).filter((c): c is AnalyticsCardItem => Boolean(c)),
    [cardIds, cardById],
  )

  const expandedItem = expandedId ? cardById.get(expandedId) : undefined

  /**
   * EVERY breakpoint, reconciled against the live card set — not just the current one.
   *
   * ⚠️ Passing only the current breakpoint is what caused the second
   * "Maximum update depth exceeded" loop on this page, and it is worth stating exactly
   * how, because the shape looks harmless. When the width changed (opening the cards
   * panel), this object went from `{lg: […]}` to `{sm: […]}` — the `lg` key
   * DISAPPEARED. react-grid-layout keeps its own breakpoint in state, updated in an
   * effect, so for one render it was still asking for `lg` from an object that no
   * longer had it. `findOrGenerateResponsiveLayout` then GENERATED an `lg` layout,
   * which set state, which re-rendered, which regenerated. Two authorities for "which
   * breakpoint are we in" that update in different phases will chase each other.
   *
   * Resolving both is cheap (two breakpoints, six cards) and removes the generation
   * path entirely: whichever breakpoint the library asks for, the answer is already
   * there. It also gives a better answer than the library's own generation would —
   * ours re-applies each card's CURRENT min/max constraints at the target column
   * count, rather than clamping a layout computed for a different one.
   */
  const responsiveLayouts = useMemo<ResponsiveLayouts<AnalyticsGridBreakpoint>>(() => {
    const sized = visible.map(c => ({ id: c.id, size: sizeOf(c) }))
    return {
      lg: resolveLayout(sized, layouts.lg, 'lg'),
      sm: resolveLayout(sized, layouts.sm ?? layouts.lg, 'sm'),
    }
  }, [visible, layouts])

  // Memoized rather than inline for the same reason as the module constants above —
  // it cannot be one of them because it depends on whether the page offers a drop
  // handler at all.
  const dropConfig = useMemo(
    () => ({ enabled: Boolean(onCardDropped), defaultItem: { w: 3, h: 1 } }),
    [onCardDropped],
  )

  /**
   * The placeholder's identity and footprint.
   *
   * ⚠️ `i` is the RESERVED `ANALYTICS_DRAG_ITEM_ID`, never the dragged card's own id —
   * that constant's own note carries the full argument, but the short version is that
   * react-grid-layout keys `filter`/`find` on this value, so an id belonging to a card
   * already on the canvas makes the library delete that card's layout entry for the
   * duration of the drag and, on a cancelled drag, permanently. Both drag origins can
   * collide: a canvas card dragged by its grip always does, and a library row does
   * whenever its metric is already placed (which VERA-260 explicitly allows).
   *
   * The SIZE still comes from the dragged card, so a KPI tile shows a 3x1 shadow and a
   * panel a 12x4 one. A single default — which is what `dropConfig.defaultItem` alone
   * would give — would promise the wrong footprint and then snap on release.
   */
  const droppingItem = useMemo<LayoutItem | undefined>(() => {
    if (!droppingCardId) return undefined
    const card = cardById.get(droppingCardId)
    if (!card) return undefined
    const size = sizeOf(card)
    return { i: ANALYTICS_DRAG_ITEM_ID, x: 0, y: 0, w: size.w, h: size.h }
  }, [droppingCardId, cardById])

  /**
   * VRG-467 — the card currently highlighted as a compose target, or null.
   *
   * Cleared whenever the drag itself ends (`droppingCardId` goes null), not only on
   * `onDragLeave`: a drop that lands elsewhere, or a drag cancelled with Escape, does
   * not reliably fire `dragleave` on the last-hovered card, and a highlight stuck on
   * after the gesture ended would be worse than a highlight that occasionally clears a
   * frame early.
   */
  const [composeHoverId, setComposeHoverId] = useState<string | null>(null)
  /**
   * VERA-260 — the same, for a card that is being hovered and REFUSING. Deliberately a
   * second piece of state rather than a flag on the first: `composeHoverId` drives the
   * brand ring that means "release here", and a refused card must never enter it. See
   * `composeRefusedPanelSx`.
   */
  const [composeRefusedHoverId, setComposeRefusedHoverId] = useState<string | null>(null)

  /**
   * ── 🚨 THIS DROP WAS A MERGE — the grid must CLEAN UP but not ACT ───────────────────
   *
   * Set by a compose-eligible card's own `onDrop`, read one bubble later by `handleDrop`.
   * It replaces the `stopPropagation()` that used to sit on that handler, and swapping the
   * one for the other fixes a real, visible defect that predates this file's current shape.
   *
   * ## What `stopPropagation` was breaking
   *
   * react-grid-layout's dropping machinery is a state machine that only ever exits through
   * its OWN `handleDrop` (or a `dragleave` that brings its enter-counter back to zero).
   * That handler calls `removeDroppingPlaceholder()`, which clears `droppingDOMNode`,
   * `droppingPosition` and the enter counter, and strips the placeholder out of the grid's
   * internal layout. Stopping the drop from reaching it means none of that happens.
   *
   * And `droppingDOMNode` is precisely what gates the effect that syncs the `layout` PROP
   * into that internal state (`if (droppingDOMNode) return`). So a merge left the grid
   * permanently deaf to its own props: the composed card — a NEW id — has no entry in the
   * stale internal layout, `processGridItem` returns null for it, and it simply is not
   * rendered. The chart the reader just dropped onto DISAPPEARS, and stays gone until the
   * page is reloaded.
   *
   * ⚠️ Measured on unmodified `HEAD`, so this is a pre-existing bug rather than a
   * consequence of unifying the drag: dragging the Certificates tile across the tile row
   * and onto the Scans chart left `droppingDOMNode` and `droppingPosition` both SET, the
   * internal layout still holding `scans_count::chart` AND `__analytics_drag_item__`, and
   * the canvas rendering four cards where there should have been five —
   * `scans_count+certificates_count::chart` present in `cardIds` and in `cards`, drawn
   * nowhere.
   *
   * It hid behind a path condition, which is why it survived: crossing ONLY eligible cards
   * on the way to the target never creates a placeholder (their `onDragOver` stops
   * propagation too), so nothing needs cleaning up. Cross one tile or one gap of empty
   * canvas — which any real drag across a dashboard does — and the placeholder exists.
   *
   * ## Why a ref rather than keeping `stopPropagation` and cleaning up by hand
   *
   * The state being cleaned belongs to the library and is not reachable from here; the only
   * supported way to leave that state machine is to let the event arrive. So the event is
   * allowed through and the grid is told, out of band, that the gesture is already spoken
   * for. `preventDefault` stays — that is what marks the card a drop target at all.
   *
   * A ref, not state: it is written and read within one event dispatch, and a re-render
   * between the two would be a race rather than a feature.
   *
   * ⚠️ `onDragOver` KEEPS its `stopPropagation`, deliberately. There the grid must not see
   * the pointer at all, or it paints a drop placeholder underneath a card the reader is
   * being told to merge into — two contradictory promises about the same release.
   */
  const composeDropHandledRef = useRef(false)

  /**
   * Does the drag currently running OFFER a merge at all?
   *
   * For a LIBRARY drag, always — `AnalyticsCustomizePanel` decides which rows are
   * draggable. For a CANVAS drag it consults the source card's own `isComposeSource`.
   *
   * ⚠️ A LIVE GATE NOW, and it was an unreachable invariant guard until the drag model was
   * unified. Every card on the canvas is draggable (moving is something any card can do),
   * so a drag whose source cannot merge — a KPI tile for a metric no chart can plot — is
   * an ordinary gesture rather than an impossible one. This is what keeps that drag silent:
   * no eligible overlays, and critically no `noEligibleComposeTargets` escalation, which
   * would otherwise paint a refusal sentence across the whole canvas in answer to a
   * question the reader never asked. A move-only drag should show move-only chrome.
   *
   * The previous arrangement reached the same outcome by never offering the gesture: the
   * shell rendered a grip only on cards that passed `isComposeSource`, so cards like
   * Completed Transfers had no drag affordance at all and could not be repositioned by
   * grip. That is the discoverability hole the unification closes.
   */
  const composeOffered = useMemo(() => {
    if (!onComposeDrop || !droppingCardId) return false
    if (!composeSourceCardId) return true
    const source = cardById.get(composeSourceCardId)
    return Boolean(source && isComposeSource(source))
  }, [onComposeDrop, droppingCardId, composeSourceCardId, cardById])
  useEffect(() => {
    if (!droppingCardId) {
      setComposeHoverId(null)
      setComposeRefusedHoverId(null)
      // Belt and braces. The flag is consumed by `handleDrop` in the same dispatch that
      // sets it; this only guarantees a gesture that somehow never reached the grid cannot
      // leave it armed to swallow the NEXT drop.
      composeDropHandledRef.current = false
    }
  }, [droppingCardId])

  /**
   * VERA-260 — is this drag being refused by EVERY card on the canvas?
   *
   * The hover gate on the refused overlay (see `composeRefusedPanelSx`'s header) exists
   * to keep prose out of an uncontested discovery pass: while there are targets the
   * reader can use, a sentence on every card they cannot competes with them. That
   * argument is conditional on there BEING something to compete with. When nothing is
   * eligible there is no discovery pass to protect — the canvas lights up nothing at
   * all, and a drag that produces no visible response anywhere is the "nothing
   * happened" failure this overlay was added to remove, reappearing in the one case
   * where the reader most needs an answer.
   *
   * So the gate inverts exactly here and nowhere else: zero eligible targets promotes
   * every refusal that HAS a sentence to immediate. Cards whose verdict carries
   * `reason: null` stay silent as always — this changes WHEN a sentence appears, never
   * WHICH cards have one.
   *
   * ⚠️ Deliberately false when no drag is running, so this never renders outside a
   * gesture: `droppingCardId` null means `composeDropVerdict` was never consulted, and
   * "no eligible targets" would be vacuously true.
   */
  const noEligibleComposeTargets = useMemo(() => {
    if (!composeOffered || !droppingCardId) return false
    return !visible.some(card => isComposeDropTarget(card, droppingCardId, composeSourceCardId))
  }, [composeOffered, droppingCardId, composeSourceCardId, visible])

  /**
   * Persist on the GESTURE, never on `onLayoutChange`.
   *
   * `onLayoutChange` also fires on mount and on every breakpoint generation, so
   * writing from it would make the live state differ from the seed the instant the
   * page rendered. `ViewsDropdown` compares by `stableStringify`, so the effect would
   * be the active view silently de-selecting itself on load — a bug this page has
   * already been bitten by once, from the other direction, with empty filter arrays.
   */
  const commit = useCallback(
    (layout: Layout) => onLayoutsChange(breakpoint, toPersistedItems(layout)),
    [breakpoint, onLayoutsChange],
  )

  /**
   * ── A DROP THAT REACHED THE GRID RATHER THAN A CHART — i.e. MOVE, not merge ─────────
   *
   * One handler, two origins, and the origin decides only WHICH state changes:
   *
   *  - a LIBRARY row is not on the canvas yet, so the drop ADDS it at `item`'s cell;
   *  - a CANVAS card is already here, so the drop MOVES it to `item`'s cell.
   *
   * That is what makes the outcome of a drag a property of where it was RELEASED rather
   * than of which affordance started it. Released on a chart or map, the card's own
   * wrapper handles it as a merge instead (`onComposeDrop`, which `stopPropagation`s, so
   * this never runs for the same gesture).
   *
   * `item` is the placeholder's entry under the reserved `ANALYTICS_DRAG_ITEM_ID` — the
   * cell react-grid-layout itself computed while previewing the drop, so what is committed
   * is exactly the position the placeholder was showing.
   *
   * The move is computed with the library's OWN primitives, deliberately: `moveElement`
   * plus the compactor are the same two calls it makes for a pointer drag, so a card
   * pushed aside here is pushed aside identically to every other way this grid re-flows.
   * `cloneLayout` first, because `moveElement` MUTATES the item it is handed (`l.x = x`)
   * and the array passed to `onDrop` is the library's live internal layout.
   *
   * ## 🚨 THE "IT CANNOT BE DRAWN" FINDING WAS WRONG. Re-measured; it draws.
   *
   * This block used to refuse to reposition at all, under a long note reporting that
   * `ResponsiveGridLayout` "does not adopt a `layouts` prop change originating from its own
   * `onDrop`" — the move persisting correctly while the grid kept painting the pre-drag
   * arrangement until an unrelated `window.resize` snapped it into place. That is not what
   * happens, and the correction matters because that note was the sole reason moving and
   * merging stayed two separate gestures.
   *
   * Re-measured in the running app at 1280x800, dragging the Scans tile onto the In
   * Progress tile: the committed layout was `scans_count x:9,y:0` with
   * `in_progress_transfers_count` displaced to `y:1`, and the DOM's inline styles read
   * `translate(881px, 0px)` and `translate(881px, 184px)` — the persisted layout, drawn,
   * with no resize and no other input. The library's own hook state, read through the
   * fiber, agreed: `GridLayout.layout`, its `prevPropsLayoutRef`, and
   * `ResponsiveGridLayout`'s `layout`/`layouts` were all already correct.
   *
   * ⚠️ The likely source of the original reading, recorded so it is not repeated: it was
   * taken from `getComputedStyle(node).transform` rather than `node.style.transform`.
   * `.react-grid-item` carries `transition: transform 200ms`, and in the Claude preview
   * pane — where `requestAnimationFrame` fires zero times, as this file's header documents
   * — a computed transform can report the START of a transition the compositor never
   * advances. A `resize` then appears to "fix" it, because `containerResizing` drops that
   * transition to `none !important`. The specified value was right the whole time.
   * **Read `style.transform`, never the computed matrix, when checking this grid's layout.**
   */
  const handleDrop = useCallback(
    (layout: Layout, item: LayoutItem | undefined) => {
      // A compose-eligible card already handled this drop and let it bubble on purpose,
      // so that react-grid-layout would tear its own dropping state down. Everything the
      // grid needed to do has now happened; this must add and move nothing.
      // See `composeDropHandledRef`.
      if (composeDropHandledRef.current) {
        composeDropHandledRef.current = false
        return
      }
      if (!item || !droppingCardId) return
      const withoutPlaceholder = layout.filter(l => l.i !== ANALYTICS_DRAG_ITEM_ID)
      if (composeSourceCardId) {
        const cols = ANALYTICS_GRID_COLS[breakpoint]
        const base = cloneLayout(withoutPlaceholder)
        const moving = base.find(l => l.i === composeSourceCardId)
        // No entry means the drag's source is not in this breakpoint's layout, which
        // cannot happen for a card the canvas is currently rendering. Bail rather than
        // commit an arrangement that would silently drop it.
        if (!moving) return
        commit(
          verticalCompactor.compact(
            moveElement(
              base,
              moving,
              item.x,
              item.y,
              true,
              false,
              verticalCompactor.type,
              cols,
              verticalCompactor.allowOverlap,
            ),
            cols,
          ),
        )
        return
      }
      onCardDropped?.(
        droppingCardId,
        { i: droppingCardId, x: item.x, y: item.y, w: item.w, h: item.h },
        breakpoint,
      )
      commit([
        ...withoutPlaceholder,
        { ...item, i: droppingCardId },
      ])
    },
    [droppingCardId, composeSourceCardId, onCardDropped, breakpoint, commit],
  )

  /**
   * Height of the expanded region.
   *
   * Measured from the canvas root's own top rather than hard-coded, because the action
   * line above it changes height (its filter chips wrap at narrow widths) and any
   * constant would be wrong at the first wrap. The root does NOT move when the hero
   * and grid hide — they are its children — so one measurement holds for the whole
   * expansion.
   *
   * A `resize` listener, deliberately NOT a `ResizeObserver`: the observer is the more
   * precise instrument, but this only needs to track the viewport, and `resize` is
   * observable in environments where `ResizeObserver` never fires.
   *
   * The scroll-to-top and the FIRST measurement now happen in `handleToggleExpand`, so
   * the region is the right height on the very first commit; this effect re-measures
   * (idempotent — same value, no extra render) and owns the resize listener. Keyboard
   * or programmatic expansion that never goes through the handler still lands here.
   */
  useLayoutEffect(() => {
    if (!expandedId) return
    // Return the page to the top BEFORE measuring, and the measurement is why.
    // `getBoundingClientRect().top` is viewport-relative, so expanding while scrolled
    // past the canvas reads a NEGATIVE top, and `100vh - (negative + 24)` is a region
    // TALLER than the viewport — which then covers the action line the expansion is
    // specifically supposed to sit below. Reproduced: expanded from a scroll offset of
    // 316px, the maximized chart started at y = 0 with the page title and filter bar
    // gone. Scrolling first also matches what the gesture means: the card is about to
    // occupy the whole content region, so there is nothing above it left to look at.
    scrollableAncestor(rootRef.current)?.scrollTo({ top: 0 })
    // Layout px, for the same reason as the click-time seed in `handleToggleExpand`:
    // `vhMinus` divides the 100vh term by the zoom but takes this offset as-is.
    const measure = () => {
      const root = rootRef.current
      setRegionTop(root ? root.getBoundingClientRect().top / zoomOf(root) : 0)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [expandedId])

  /**
   * Run the pending expand/collapse animation, once the new tree is laid out.
   *
   * A layout effect so the measurement is taken after layout but before paint, which is
   * what stops the target appearing at its destination for one frame before jumping
   * back to the source.
   *
   * The retry exists for the COLLAPSE direction only, and it is not defensive padding.
   * react-grid-layout gates its own children behind an internal `mounted` state, so a
   * card returning to the grid appears one commit later than the collapse — the same
   * measurement that forced `autoFocusExpandControl` to be a prop rather than a
   * `querySelector` from here. Four attempts at ~16ms is ~2 frames past that; beyond
   * that the animation is skipped rather than fired late, because an animation that
   * starts after the eye has already settled reads as a glitch, not as motion.
   *
   * `prefers-reduced-motion` is honoured by consuming the request and doing nothing —
   * the state change has already happened, so the result is the instant swap that
   * setting asks for.
   */
  useLayoutEffect(() => {
    const pending = pendingFlip.current
    if (!pending) return
    pendingFlip.current = null
    if (prefersReducedMotion()) return

    const selector = expandedId
      ? `[${EXPANDED_NODE_ATTR}]`
      : `[${CARD_NODE_ATTR}="${CSS.escape(pending.id)}"]`

    let attempts = 0
    let timer = 0
    const attempt = () => {
      const node = rootRef.current?.querySelector<HTMLElement>(selector)
      if (node) {
        flipFrom(node, pending.rect)
        return
      }
      if (++attempts < 4) timer = window.setTimeout(attempt, 16)
    }
    attempt()
    return () => window.clearTimeout(timer)
  }, [expandedId])

  /**
   * Which card should take focus once it is back in the grid.
   *
   * Set on collapse, consumed by that card's shell on mount, cleared when it reports
   * back. It has to be a request rather than an imperative focus call because the
   * button does not exist yet at this point — see `autoFocusExpandControl` on
   * `AnalyticsCardShell` for the measurement that established that.
   */
  const [restoreFocusId, setRestoreFocusId] = useState<string | null>(null)
  const previousExpandedId = useRef<string | null>(null)

  useEffect(() => {
    const previous = previousExpandedId.current
    previousExpandedId.current = expandedId
    if (previous && !expandedId) setRestoreFocusId(previous)
  }, [expandedId])

  const clearRestoreFocus = useCallback(() => setRestoreFocusId(null), [])

  // The one remaining branch that unmounts the container. Safe only because the page
  // never passes `isLoading` — the canvas is mounted once the page is. If a caller ever
  // does pass it, this must become a `visibility` swap for the reason in the header.
  if (isLoading) return <AnalyticsCanvasSkeleton />

  const isExpanded = Boolean(expandedId && expandedItem)

  return (
    <Box ref={rootRef}>
      {/* ---------------------------------------------------------------------------
          Expanded: one card owns the region, rendered ABOVE the grid container rather
          than instead of it, so `rootRef`'s top is still the region's top and the
          height measurement is unchanged.
          --------------------------------------------------------------------------- */}
      {isExpanded && expandedItem && (
        <Box
          {...{ [EXPANDED_NODE_ATTR]: '' }}
          sx={{ height: vhMinus(regionTop + PAGE_GUTTER_PX), minHeight: 320 }}
        >
          <AnalyticsCardShell
            id={expandedItem.id}
            title={expandedItem.title}
            description={expandedItem.description}
            action={expandedItem.action}
            kind={expandedItem.kind}
            surface={expandedItem.surface}
            draggable={false}
            expandable
            expanded
            onToggleExpand={handleToggleExpand}
          >
            {expandedItem.render({ expanded: true, fillHeight: true })}
          </AnalyticsCardShell>
        </Box>
      )}

      {/* NO HERO SLOT. The ROI hero used to render here, pinned above the grid, and it
          now renders on the PAGE — above the sticky control bar, because nothing in that
          bar scopes it: the Scans/Certificates toggle moved into the two panel headers, and
          the hero owns its own fixed all-time window rather than following the date picker.
          A control sitting above a card it does not affect is misleading, which is also why
          the date picker was moved DOWN into that bar rather than the hero being moved
          below it (see `AnalyticsFiltersHeader.dateRangeControl`). The page hides the hero
          while a card is expanded and tells this component how tall it was, so the expanded
          region still starts immediately below the action line; see
          `collapsingChromeAbovePx`. */}

      {/* 🚨 UNCONDITIONAL. This div is what `useContainerWidth` measures and what its
          ResizeObserver is bound to; taking it out of the DOM for the duration of an
          expansion is what used to wipe the dashboard on collapse (see the header).
          While expanded it renders empty and therefore collapses to zero height, so it
          costs no layout — but it keeps its width, its identity and its observer. */}
      <Box
        ref={containerRef}
        sx={{
          /**
           * react-grid-layout's own stylesheet, re-pointed at house tokens. Its
           * defaults are a RED drop placeholder and a black resize chevron, both of
           * which are wrong here and the second of which is invisible in dark mode.
           *
           * ⚠️ `brand.600`, NOT `brand.main`. The palette's `brand` is a SCALE with
           * numbered stops, so `brand.main` resolves to nothing and MUI drops the
           * declaration in silence — measured, the placeholder was still computing to
           * `rgb(255, 0, 0)` while the `opacity` and `borderRadius` on the same rule
           * applied correctly, which is what makes this failure so easy to miss.
           */
          '& .react-grid-item.react-grid-placeholder': {
            backgroundColor: 'brand.600',
            opacity: 0.12,
            borderRadius: 3,
          },
          /*
            See `containerResizing` for the full reasoning. In short: while the CONTAINER
            is resizing, every card keeps its grid cell and only the cell's pixel size
            moves — so there is nothing to animate, and animating it makes each card lag
            its own content. The library's 200ms ease is kept for the case it is for
            (a drag re-flowing its neighbours) and dropped for this one.

            `!important` because the duration is set by react-grid-layout's own stylesheet
            on the same element, at equal specificity — without it, whichever sheet loads
            last wins, which is not a thing to leave to chance.
          */
          /*
            ⚠️ BOTH RULES, and the second one was the bug that survived the first fix.
            The vendor sheet carries TWO transitions, not one:

              .react-grid-layout { transition: height 200ms ease }      <- the CONTAINER
              .react-grid-item   { transition: transform, width, height 200ms ease }

            Only the item rule was ever overridden here, so the grid's own HEIGHT went on
            easing through every resize. A collapse that widens the cards shortens the
            grid, and the container then took 200ms to reach its new height while the
            cards inside it had already arrived — which is the same "moves slower than
            the sidebar" complaint, one level up, and it is the more visible of the two
            because everything BELOW the canvas is pushed by that height.

            A DESCENDANT selector for both. This `sx` sits on the wrapping `Box` that
            `useContainerWidth` measures (`ref={containerRef}`) — `ResponsiveGridLayout`
            renders inside it, so the `react-grid-layout` class is on a child. `&.` would
            silently match nothing.
          */
          ...(containerResizing && {
            '& .react-grid-layout': { transition: 'none !important' },
            '& .react-grid-item': { transition: 'none !important' },
          }),
          '& .react-grid-item.react-draggable-dragging': {
            // The lifted state. Border only — no shadow, in keeping with the flat
            // surface rule the shared Card states for itself.
            '& > .MuiPaper-root, & > * > .MuiPaper-root': { borderColor: 'brand.600' },
          },
          '& .react-grid-item > .react-resizable-handle::after': {
            borderRightColor: 'text.disabled',
            borderBottomColor: 'text.disabled',
          },
        }}
      >
        {/* `width > 0` is not belt-and-braces on top of `mounted` — the two say
            different things. `mounted` means "measured at least once", and a
            measurement of ZERO satisfies it. Laying the grid out against a zero width
            gives every column zero width and stacks the cards on top of each other,
            so the skeleton holds until there is a real box to lay out in. */}
        {!isExpanded && mounted && width > 0 && (
        <ResponsiveGridLayout<AnalyticsGridBreakpoint>
          width={width}
          /**
           * NO `breakpoint` prop. The library derives it from `width` with the same
           * `getBreakpointFromWidth(ANALYTICS_GRID_BREAKPOINTS, width)` call this
           * component uses above, so the two agree by construction. Passing it as well
           * makes the prop and the library's internal state two authorities that
           * update in different phases — see `responsiveLayouts` for what that cost.
           */
          breakpoints={ANALYTICS_GRID_BREAKPOINTS}
          cols={ANALYTICS_GRID_COLS}
          layouts={responsiveLayouts}
          rowHeight={ANALYTICS_ROW_HEIGHT}
          margin={ANALYTICS_GRID_MARGIN}
          // Zero, because the page already owns its gutter — `Layout`'s scroll-container
          // `p: 6`. Container padding here would double it on the outside edges only, which
          // is the same defect the page's own removed `p={6}` was producing one level up.
          containerPadding={CONTAINER_PADDING}
          /**
           * ⚠️ PASSED EXPLICITLY, and this line is load-bearing.
           *
           * Left off, `ResponsiveGridLayout` builds its own with
           * `compactorProp ?? getCompactor('vertical')` — unmemoized, so a NEW OBJECT
           * on every render. That object is a dependency of `useGridLayout`'s
           * `setLayout` callback, so `setLayout` also changes identity on every
           * render, so the effect that syncs the `layout` PROP into internal state
           * re-runs on every render. And that effect's guard —
           * `deepEqual(propsLayout, prevLayoutRef.current)` — can never be satisfied,
           * because it compares the layout we pass in against the library's own
           * CLONED AND COMPACTED copy of it. The result is `setLayoutState` on every
           * render, forever: "Maximum update depth exceeded", which is exactly what
           * this page was throwing.
           *
           * `verticalCompactor` is the same default, imported from the module, so the
           * behaviour is unchanged and only the identity is stable.
           */
          compactor={verticalCompactor}
          /**
           * ⚠️ The half of the sidebar-sync fix that the `transition: none` rules below
           * cannot supply on their own — see `fluidPositionStrategy` for the measurement
           * that separates "the cards were EASED late" from "the cards were POSITIONED
           * late". Both are true; only this one addresses the second.
           */
          positionStrategy={positionStrategy}
          dragConfig={DRAG_CONFIG}
          resizeConfig={RESIZE_CONFIG}
          dropConfig={dropConfig}
          droppingItem={droppingItem}
          // NO `onDragStop`. react-grid-layout's own dragging is off (`DRAG_CONFIG`) —
          // a card is moved by dropping it, so `handleDrop` is where a move is committed.
          onResizeStop={commit}
          onDrop={handleDrop}
        >
          {/* Children come from the CARD list, not from a breakpoint's layout.
              react-grid-layout matches a child to its layout item by `key`, so the
              order here carries no meaning — and rendering from the layout would make
              the child list change identity every time the breakpoint did, for no
              gain. */}
          {visible.map(card => {
            // VRG-467 — is THIS drag, over THIS card, a valid compose drop? Recomputed
            // per card per render rather than memoized: it is a cheap string/set check
            // against at most a handful of visible panels, and memoizing it would need
            // its own dependency array keyed on `droppingCardId` anyway.
            const composeVerdict =
              composeOffered && droppingCardId
                ? composeDropVerdict(card, droppingCardId, composeSourceCardId)
                : null
            const composeEligible = composeVerdict?.ok === true
            // The sentence to show if the pointer rests here, or null to stay silent.
            // See `composeDropVerdict`'s header for why silence is a design choice and
            // not simply the absence of a message.
            const composeRefusal =
              composeVerdict && !composeVerdict.ok ? composeVerdict.reason : null
            // VERA-260 — does THIS card's drag ALSO offer a merge? Wording only now: every
            // card gets the same grip and the same drag (see `AnalyticsCardShell`), and
            // this decides whether the tooltip and accessible name mention adding to a
            // chart. `onComposeDrop` is required alongside the capability because without
            // it nothing can act on the release, so promising the outcome would be a lie.
            const canComposeSource = Boolean(onComposeDrop) && isComposeSource(card)
            return (
              // The attribute is how the expand animation finds where this card sat.
              // On the wrapper rather than the shell because it is the wrapper that
              // react-grid-layout positions, so its rect IS the card's grid cell.
              <Box
                key={card.id}
                {...{ [CARD_NODE_ATTR]: card.id }}
                sx={{
                  minWidth: 0,
                  // react-grid-layout positions this element with `position: absolute`
                  // via an inline style it injects, which already establishes a valid
                  // containing block for the overlay below — `relative` here is a
                  // documented fallback for that, not load-bearing on its own.
                  position: 'relative',
                  ...(composeHoverId === card.id && COMPOSE_DROP_TARGET_SX),
                }}
                /**
                 * VRG-467 — the compose-onto-a-card drop target.
                 *
                 * This wrapper sits INSIDE the grid container react-grid-layout itself
                 * listens on, so both handlers have to answer the question "does the grid
                 * get to see this too?" — and the two answers are DIFFERENT:
                 *
                 *  - `onDragOver` STOPS propagation. The grid must not track the pointer
                 *    across an eligible card, or it paints a drop placeholder underneath
                 *    the very card that is telling the reader to merge into it.
                 *  - `onDrop` lets the event through, and hands the grid a ref
                 *    (`composeDropHandledRef`) saying the gesture is already spoken for.
                 *    The grid needs the event to tear down its own dropping state; the ref
                 *    is what stops it also creating a card. That asymmetry is deliberate
                 *    and load-bearing — see the ref's own note for the defect that
                 *    `stopPropagation` on this handler was causing.
                 *
                 * Both are wired only when this specific card is a valid target for whatever
                 * is being dragged, so an ordinary drop-to-canvas (nothing dragged onto a
                 * card, or a drag the card cannot accept) is never intercepted at all.
                 */
                onDragOver={
                  composeEligible
                    ? e => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (composeHoverId !== card.id) setComposeHoverId(card.id)
                      }
                    : composeRefusal
                      ? /* ⚠️ NEITHER `preventDefault` NOR `stopPropagation` — see
                           `composeRefusedPanelSx`'s header. This card is not a drop
                           target, so the browser must keep showing the "no drop" cursor
                           and the event must keep reaching the grid's own drop-to-canvas
                           handler. State only. */
                        () => {
                          if (composeRefusedHoverId !== card.id) {
                            setComposeRefusedHoverId(card.id)
                          }
                        }
                      : undefined
                }
                onDragLeave={
                  composeHoverId === card.id
                    ? () => setComposeHoverId(null)
                    : composeRefusedHoverId === card.id
                      ? () => setComposeRefusedHoverId(null)
                      : undefined
                }
                onDrop={
                  composeEligible
                    ? e => {
                        e.preventDefault()
                        /*
                          ⚠️ NO `stopPropagation` — and its absence is the fix, not an
                          oversight. The drop MUST reach the grid so react-grid-layout can
                          tear down its own dropping state; blocking it left that state set
                          forever and stopped the grid ever adopting a layout prop again,
                          so the composed card was never drawn. `composeDropHandledRef` is
                          what stops the grid ALSO acting on the same gesture.
                        */
                        composeDropHandledRef.current = true
                        onComposeDrop?.(card.id, composeSourceMetricId(droppingCardId as string))
                        setComposeHoverId(null)
                      }
                    : undefined
                }
              >
                {/* VERA-260 — see `composeDropScrimSx`'s header. Rendered for EVERY
                    eligible card as soon as the drag starts, not only the hovered one —
                    that is the discovery fix. `aria-hidden`: the drop is already
                    announced by the drag itself, and this is a purely visual cue. */}
                {composeEligible && (
                  <Box
                    aria-hidden
                    sx={composeDropScrimSx(theme, composeHoverId === card.id)}
                  >
                    {/*
                      The GLASS panel — see the overlay header for why it is theme-aware
                      now rather than translucent-white-over-a-dark-wash.

                      ⚠️ SIZED AS A DROPZONE, not as a toast. It was a single 32px-tall
                      row: an icon and a line of text, which at the centre of a
                      full-width chart card read as a notification that had appeared
                      over the card rather than as a REGION to release onto. A dropzone
                      has to look like a place. So it is a column — hand, then label —
                      with a floor width and generous padding, which is the same
                      proportion `FileUploadField`'s own dropzone uses.

                      `maxWidth: '80%'` rather than a fixed cap: this same overlay
                      renders on a 6-column half-width card and on a 12-column one, and
                      a fixed width that reads as generous on the wide one overflows the
                      narrow one. The floor and the ceiling are both relative to the
                      card, so the panel scales with whatever it is centred in.
                    */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        px: 8,
                        py: 6,
                        minWidth: 220,
                        maxWidth: '80%',
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor:
                          composeHoverId === card.id
                            ? 'brand.500'
                            : alpha(theme.palette.text.primary, 0.18),
                        // 0.72 is a FLOOR, not a taste — see the overlay header. The
                        // panel is what buys legibility now that the scrim no longer
                        // dims, so it has to stay opaque enough to sit over a busy
                        // chart in either theme.
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          composeHoverId === card.id ? 0.82 : 0.72,
                        ),
                        backdropFilter: COMPOSE_DROP_GLASS_BLUR,
                        WebkitBackdropFilter: COMPOSE_DROP_GLASS_BLUR,
                        boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.28)}`,
                        color: 'text.primary',
                        textAlign: 'center',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                      }}
                    >
                      <ComposeDropHand hovered={composeHoverId === card.id} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit' }}>
                        {COMPOSE_DROP_LABEL}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* VERA-260 — the REFUSED counterpart. Shown when the verdict carried a
                    sentence AND either the pointer is resting here, OR nothing on the
                    canvas can take this drag at all. See `composeRefusedPanelSx`'s header
                    for why the hover gate exists, and `noEligibleComposeTargets` for the
                    one case that lifts it. */}
                {composeRefusal && (composeRefusedHoverId === card.id || noEligibleComposeTargets) && (
                  <Box aria-hidden sx={composeDropScrimSx(theme, false)}>
                    <Box sx={composeRefusedPanelSx(theme)}>
                      <BanIcon size={28} strokeWidth={1} />
                      <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {composeRefusal}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <AnalyticsCardShell
                  id={card.id}
                  title={card.title}
                  description={card.description}
                  action={card.action}
                  kind={card.kind}
                  surface={card.surface}
                  // No `onComposeDragStart` means nothing can observe that a drag has
                  // begun, so no drop of any kind can be resolved — the card must not
                  // offer a gesture that reports nothing and lands nowhere.
                  draggable={Boolean(onComposeDragStart)}
                  expandable={card.expandable}
                  expanded={false}
                  onToggleExpand={handleToggleExpand}
                  autoFocusExpandControl={restoreFocusId === card.id}
                  onExpandControlFocused={clearRestoreFocus}
                  onRemove={onRemoveCard}
                  composeSourceEnabled={canComposeSource}
                  onCardDragStart={onComposeDragStart}
                  onCardDragEnd={onComposeDragEnd}
                >
                  {/* Every gridded PANEL fills its cell: the cell is what a resize
                      changes, so a panel that kept an intrinsic height would make the
                      vertical handle do nothing visible. Tiles keep their own 160px
                      block, which is also exactly one grid row. */}
                  {card.render({ expanded: false, fillHeight: card.kind === 'panel' })}
                </AnalyticsCardShell>
              </Box>
            )
          })}
        </ResponsiveGridLayout>
        )}
      </Box>
    </Box>
  )
}

/**
 * Footprint parity (§19): a row of four stat tiles at one grid row each, then two
 * full-width panels — the default view's own arrangement, at the same heights and the
 * same 24px gaps, so nothing shifts when the data lands.
 *
 * NO hero band. The hero is no longer a child of this component and draws its own
 * skeleton at its own position on the page (`RoiHero`), so a band here would reserve its
 * height twice.
 */
export const AnalyticsCanvasSkeleton = () => (
  <Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: `${ANALYTICS_GRID_MARGIN[0]}px`,
      }}
    >
      {[0, 1, 2, 3].map(i => (
        <Skeleton key={i} variant="rounded" height={ANALYTICS_ROW_HEIGHT} sx={{ borderRadius: 3 }} />
      ))}
    </Box>

    <Box
      sx={{
        mt: `${ANALYTICS_GRID_MARGIN[1]}px`,
        display: 'grid',
        gap: `${ANALYTICS_GRID_MARGIN[1]}px`,
      }}
    >
      {[0, 1].map(i => (
        <Skeleton
          key={i}
          variant="rounded"
          // Four rows plus the three gaps between them — the panel default.
          height={ANALYTICS_ROW_HEIGHT * 4 + ANALYTICS_GRID_MARGIN[1] * 3}
          sx={{ borderRadius: 3 }}
        />
      ))}
    </Box>
  </Box>
)
